import React, { useEffect, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"

import dayjs from "dayjs"
import moment from "moment"
import clsx from "clsx"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import IconButton from "@mui/material/IconButton"
import { Box, DialogContent, Stack } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import withStyles from "@mui/styles/withStyles"

import { getScreen } from "../../../reducers/Utils/app"
import { COLORS } from "../../../utils"
import NumericKeypad from "./NumericKeypad"
import InternalDLCField from "./InternalDLCField"
import PrinterSelect from "./printerSelect"
import { searchPrinters, fetchFriendlyNames, getPrinterLabel, buildCardLabelZplFromData, getLastSelectedPrinterUid, setLastSelectedPrinterUid } from "../../../utils/zebraPrinters"
import { readStatus, sendZpl } from "../../../utils/browserPrint"
import { computePrintableLabelsForPSEs } from "./printableLabels"
import { PRINT_MESSAGES, getPrintErrorMessage, getPrinterProblemMessage, getPrintSuccessMessage } from "./printMessages"

const getRecipeNearestDLC = (dlc = []) => {
  if (dlc.length === 0) return dayjs()
  const minDLC = dlc.reduce((min, current) => {
    return current.value < min.value ? current : min
  }, dlc[0])

  return dayjs().add(minDLC.value, "day")
}

// After a successful send, taps within this delay are a double tap, not a new batch
const PRINT_COOLDOWN_MS = 2000

// pses: several steps printed at once (unpacking), in place of pse
const ModalPrintPackagingExecution = ({ classes, open, onClose, pse, pses }) => {
  const [numberOfLabels, setNumberOfLabels] = useState("")
  const [internalDLC, setInternalDLC] = useState(dayjs())
  const [availablePrinters, setAvailablePrinters] = useState([])
  const [printablePSEs, setPrintablePSEs] = useState([])
  
  // The modal is mounted on opening only: read the printer chosen last time (kept in localStorage)
  const [selectedPrinterUid, setSelectedPrinterUidState] = useState(() => getLastSelectedPrinterUid())

  // Wrap the setter so every change is also persisted
  const setSelectedPrinterUid = (uid) => {
    setLastSelectedPrinterUid(uid)
    setSelectedPrinterUidState(uid)
  }

  // true from the mount, since the first search starts right away
  const [loadingPrinters, setLoadingPrinters] = useState(true)
  const searchingRef = useRef(false)
  // Non-null string = why the search failed (Browser Print missing or silent, no printer)
  const [searchError, setSearchError] = useState(null)
  // { type: "error" | "success", text } about the last print
  const [printMessage, setPrintMessage] = useState(null)
  const [printing, setPrinting] = useState(false)
  // Blocks a second tap before the printing state is rendered
  const printingRef = useRef(false)
  const screen = useSelector(getScreen)

  // Derive the currently selected printer object from its UID
  const selectedPrinter = useMemo(
    () => availablePrinters.find(printer => printer?.uid === selectedPrinterUid) || null,
    [availablePrinters, selectedPrinterUid]
  )
  // The chosen printer is missing from a successful search: never replace it by another one
  const selectedPrinterMissing = !loadingPrinters &&!searchError && !!selectedPrinterUid && !selectedPrinter
  const printerSelectError = searchError || (selectedPrinterMissing ? PRINT_MESSAGES.printerNotFound : null)

  useEffect(() => {
    // Ignore the result of a computation started for a previous PSE
    let cancelled = false
    const computePrintablePSEs = async () => {
      const labels = await computePrintableLabelsForPSEs(pses || [pse])
      if (!cancelled) setPrintablePSEs(labels)
    }

    computePrintablePSEs()
    return () => { cancelled = true }
  }, [pse, pses])

  useEffect(() => {
    if (!pse?.isSectionLastStep) return
    if (!pse?.recipe?.dlc) return
    setInternalDLC(getRecipeNearestDLC(pse.recipe.dlc))
  }, [pse?.recipe?.dlc, pse?.isSectionLastStep])

  // Search the Zebra printers and show them as soon as the search ends; names are read in background.
  // forceRefresh=true (manual "Actualiser" button only) bypasses the 5 min cache and reads every name again.
  // Every Browser Print call has a time limit, so the search always ends.
  const refreshPrinters = async (forceRefresh = false) => {
    if (searchingRef.current) return
    searchingRef.current = true
    setSearchError(null)
    setPrintMessage(null)
    setLoadingPrinters(true)
    try {
      const printers = await searchPrinters(forceRefresh)
      setAvailablePrinters(printers)
      fetchFriendlyNames(printers, (uid, friendlyName) => {
        setAvailablePrinters(current => current.map(printer => printer.uid === uid ? { ...printer, friendlyName } : printer))
      }, forceRefresh)
    } catch (e) {
      console.error("Printer search failed", e)
      setAvailablePrinters([])
      setSearchError(getPrintErrorMessage(e))
    } finally {
      searchingRef.current = false
      setLoadingPrinters(false)
    }
  }

  // One search on opening (the modal is mounted on opening only); afterwards, only "Actualiser" searches again
  useEffect(() => {
    refreshPrinters()
  }, [])

  /**
   * Sends the card labels of the PSE to the selected Zebra printer, in one payload,
   * after checking its status (paper, head, ribbon, pause). One tap prints one batch.
   */
  const onPrint = async numberOfLabels => {
    if (printingRef.current) return
    setPrintMessage(null)

    if (loadingPrinters) {
      setPrintMessage({ type: "error", text: PRINT_MESSAGES.searching })
      return
    }
    if (!selectedPrinterUid) {
      setPrintMessage({ type: "error", text: PRINT_MESSAGES.noPrinterSelected })
      return
    }
    if (!selectedPrinter) {
      setPrintMessage({ type: "error", text: searchError || PRINT_MESSAGES.printerNotFound })
      return
    }
    if (printablePSEs.length === 0) {
      setPrintMessage({ type: "error", text: PRINT_MESSAGES.labelsNotReady })
      return
    }

    printingRef.current = true
    setPrinting(true)
    let sent = false
    try {
      // Normalize copies: integer >= 1
      const copies = Math.max(1, (parseInt(numberOfLabels, 10) || 0))

      // Build one payload with proper per-label pagination (1/n, 2/n, …)
      let payload = ""
      for (let i = 1; i <= copies; i++) {
        for (const pPse of printablePSEs) {
          const zpl = buildCardLabelZplFromData({ ...pPse, dlc: internalDLC }, i, copies)
          payload += zpl
        }
      }

      // BrowserPrint's send() succeeds even when the printer is off or not ready: read its status first
      const problem = getPrinterProblemMessage(await readStatus(selectedPrinter))
      if (problem) {
        setPrintMessage({ type: "error", text: problem })
        return
      }

      // One round-trip to the printer (faster on Android tablets)
      await sendZpl(selectedPrinter, payload)
      sent = true

      setPrintMessage({ type: "success", text: getPrintSuccessMessage(copies * printablePSEs.length, getPrinterLabel(selectedPrinter)) })
    } catch (e) {
      console.error("Print failed", e)
      setPrintMessage({ type: "error", text: getPrintErrorMessage(e) })
    } finally {
      const release = () => {
        printingRef.current = false
        setPrinting(false)
      }
      // The send can end before a quick second tap: keep the button locked a moment after a success,
      // so that a double tap prints one batch. After a failure, the operator can retry at once
      if (sent) setTimeout(release, PRINT_COOLDOWN_MS)
      else release()
    }
  }

  const numberOfUniqueLables = useMemo(() => printablePSEs.length, [printablePSEs])

  return (
    <Dialog
      open={open}
      className={clsx(classes.dialog)}
      PaperProps={{
        sx: screen !== "desktop" ? {
          position: "fixed",
          bottom: 0,
          left: "50%",
          transform: "translateX(-50%)",
          margin: 0,
          borderRadius: "24px 24px 0 0",
          maxWidth: "100% !important",
        } : { borderRadius: "24px" },
      }}
      onClose={onClose}
      onClick={(evt) => evt.stopPropagation()}>
      <DialogTitle id="modal-print-pe">
        Imprimer
      </DialogTitle>
      <IconButton
        aria-label="close"
        className={classes.iconDelete}
        onClick={onClose}
      >
        <CloseIcon sx={{ fontSize: "32px" }} />
      </IconButton>
      <DialogContent sx={{ padding: "10px 24px 24px 24px" }}>
        <div className={clsx("flexRow", "stretch")}>
          <div className={clsx(classes.printPreview)}>
            <div className={clsx(classes.printPreviewContent)}>
              {printablePSEs.map((pPse, index) => (
                <div key={index} className={clsx(classes.printPreviewContentDetails)}>
                  <div>{pPse.title} {pPse.packagingDateLabel && ` (${pPse.packagingDateLabel})`}</div>
                  <div>Fait le : {moment(pPse.stepProductionDate).format("DD/MM/YY")}</div>
                  <div>DLC interne : {dayjs(internalDLC).format("DD/MM/YYYY")}</div>
                  <div>Poids: {Math.round((pPse.netWeight) * 100) / 100} kg</div>
                  <div></div>
                  <div>{index + 1}/{numberOfUniqueLables}</div>
                </div>
              ))}
            </div>
            <div className={clsx(classes.printPreviewFooter)}>{numberOfUniqueLables} étiquette{numberOfUniqueLables > 1 && "s"}</div>
          </div>
          <Stack className={clsx(classes.printParams)}>
            <div>
              <PrinterSelect
                printers={availablePrinters}
                selectedPrinterUid={selectedPrinterUid}
                onChange={(uid) => { setSelectedPrinterUid(uid); setPrintMessage(null) }}
                loading={loadingPrinters}
                onRefresh={() => refreshPrinters(true)}
                selectedPrinterMissing={selectedPrinterMissing}
              />
              {printerSelectError && (
                <Box sx={{ mt: 1, fontSize: 12, color: COLORS.RED_ERROR, lineHeight: 1.4 }}>
                  {printerSelectError}
                </Box>
              )}
              {printMessage && printMessage.text !== printerSelectError && (
                <Box sx={{ mt: 1, fontSize: 12, color: printMessage.type === "success" ? COLORS.GREEN_SUCCESS_800 : COLORS.RED_ERROR, lineHeight: 1.4 }}>
                  {printMessage.text}
                </Box>
              )}
            </div>
            <div className={clsx(classes.inputContainer, classes.internalDLC)}>
              <InternalDLCField
                defaultValue={internalDLC}
                onChange={setInternalDLC}
                defaultStartDate={dayjs()}
                maxWidth="100%"
              />
            </div>
            <div>
              <NumericKeypad
                classes={classes}
                onPrint={onPrint}
                printDisabled={printing}
                value={numberOfLabels}
                onChange={setNumberOfLabels}
              />
            </div>
          </Stack>
        </div>
      </DialogContent>
    </Dialog>
  )
}

const BREAK_POINT = "@media (max-width: 750px) or ( max-height: 750px)"
const styles = (theme) => ({
  dialog: {
    "& .MuiPaper-root": {
      maxWidth: 1024,
      width: 1024,
      minHeight: "772px",
      maxHeight: "99vh",
      overflowY: "visible",
    }
  },
  iconDelete: {
    position: "absolute",
    right: 8,
    top: 8,
    color: "#555555",
  },
  internalDLC: {
    marginTop: 24,
    "& .MuiFormControl-root": {
      width: "100%",
    }
  },
  printPreview: {
    position: "relative",
    composes: "flexColumn center flex1",
    // width: 420,
    background: theme.palette.grey[500],
    marginRight: 40,
    fontSize: 13,
    maxHeight: "674px", // Ensure the preview has a max height
    overflowY: "auto", // Enable vertical scrolling
    [BREAK_POINT]: {
      marginRight: 24
    }
  },
  printParams: {
    width: 327,
    flex: "0 0 1 auto",
  },
  printPreviewContent: {
    composes: "flexCenter",
    flex: 1,
    padding: 16,
    gap: 16,
  },
  printPreviewContentDetails: {
    padding: "16px 16px 20px 16px",
    background: COLORS.WHITE,
    width: 248,
    maxWidth: "100%",
    borderRadius: 5,
    position: "relative",
    "& > div:not(:last-of-type)": {
      borderBottom: "1px solid #DEDEDE",
      minHeight: 28,
      padding: "4px 0",
      "&:first-of-type": {
        borderBottom: "1px solid #D9D9D9",
      },
    },
    "& > div:last-of-type": {
      position: "absolute",
      bottom: 4,
      right: 8,
      fontSize: 10,
    }
  },
  printPreviewFooter: {
    padding: "8px 16px",
    background: "#DBDBDB",
    borderRadius: 20,
    position: "sticky",
    bottom: 16,
    left: "50%",
    transform: "translateX(-50%)",
    width: "fit-content",
    marginLeft: "auto",
    marginRight: "auto",
  },
  inputContainer: {
    // margin: 8
  },
  calculator: {
    marginTop: 16,
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gridTemplateRows: "repeat(4, 1fr)",
    gap: 24,
    [BREAK_POINT]: {
      gap: 16,
    }
  },
  calculatorInput: {
    "& input": {
      fontSize: 40
    }
  },
  calculatorButton: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    border: `1px solid ${theme.palette.grey[500]}`,
    borderRadius: 5,
    fontSize: 24,
    color: theme.palette.white,
    cursor: "pointer",
    aspectRatio: 1,
    "&:nth-last-of-type(3)": {
      border: `1px solid ${COLORS.DOWNLOAD_DOCUMENT_BLUE}`,
      color: COLORS.DOWNLOAD_DOCUMENT_BLUE
    },
    "&:last-of-type": {
      background: COLORS.DOWNLOAD_DOCUMENT_BLUE,
      border: `1px solid ${COLORS.DOWNLOAD_DOCUMENT_BLUE}`,
      color: COLORS.WHITE
    },
    "@media (hover: hover)": {
      "&:hover": {
        background: COLORS.DOWNLOAD_DOCUMENT_BLUE,
        border: `1px solid ${COLORS.DOWNLOAD_DOCUMENT_BLUE}`,
        color: COLORS.WHITE
      },
    },
    "&.disabled,&.disabled:hover": {
      background: COLORS.BACKGROUND_LIGHT_GREY,
      border: `1px solid ${COLORS.BACKGROUND_LIGHT_GREY}`,
      color: "#7B7B7B",
      cursor: "not-allowed",
    },
  }
})

export default withStyles(styles)(ModalPrintPackagingExecution)